import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, MapPin, Calendar, Users, Tag, Sun, Cloud, Accessibility, Baby, Heart } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FilterPanel = ({ isOpen, onClose, filters, onFilterChange, onApply, onReset }) => {
  const [categories, setCategories] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    distance: true,
    age: true,
    price: true,
    category: false,
    features: false
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/api/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const ageGroups = [
    { id: '0-2', label: 'Babies & Toddlers', sublabel: '0-2 years' },
    { id: '3-5', label: 'Preschool', sublabel: '3-5 years' },
    { id: '6-8', label: 'Early Primary', sublabel: '6-8 years' },
    { id: '9-12', label: 'Tweens', sublabel: '9-12 years' },
    { id: '13+', label: 'Teens', sublabel: '13+ years' }
  ];

  const priceOptions = [
    { id: 'free', label: 'Free', color: 'text-emerald-600' },
    { id: 'budget', label: 'Budget', sublabel: 'Under £10', color: 'text-[#81B29A]' },
    { id: 'mid-range', label: 'Mid-Range', sublabel: '£10-30', color: 'text-[#666B67]' },
    { id: 'premium', label: 'Premium', sublabel: '£30+', color: 'text-[#E07A5F]' }
  ];

  const FilterSection = ({ title, icon: Icon, id, children }) => (
    <div className="border-b border-[#2C302E]/5 py-4">
      <button 
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`filter-section-${id}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#81B29A]" />
          <span className="font-medium text-[#2C302E]">{title}</span>
        </div>
        {expandedSections[id] ? (
          <ChevronUp className="w-5 h-5 text-[#666B67]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#666B67]" />
        )}
      </button>
      {expandedSections[id] && (
        <div className="mt-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-white">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-2xl font-semibold text-[#2C302E]">Filters</SheetTitle>
        </SheetHeader>

        {/* Distance */}
        <FilterSection title="Distance" icon={MapPin} id="distance">
          <div className="px-2">
            <Slider
              value={[filters.radius || 25]}
              onValueChange={(value) => onFilterChange({ ...filters, radius: value[0] })}
              max={100}
              min={1}
              step={1}
              className="mb-2"
              data-testid="filter-distance-slider"
            />
            <div className="flex justify-between text-sm text-[#666B67]">
              <span>1 km</span>
              <span className="font-medium text-[#E07A5F]">{filters.radius || 25} km</span>
              <span>100 km</span>
            </div>
          </div>
        </FilterSection>

        {/* Age Groups */}
        <FilterSection title="Age Suitability" icon={Users} id="age">
          <div className="space-y-3">
            {ageGroups.map((age) => (
              <label 
                key={age.id}
                className="flex items-center gap-3 cursor-pointer"
                data-testid={`filter-age-${age.id}`}
              >
                <Checkbox
                  checked={filters.ageGroups?.includes(age.id)}
                  onCheckedChange={(checked) => {
                    const current = filters.ageGroups || [];
                    if (checked) {
                      onFilterChange({ ...filters, ageGroups: [...current, age.id] });
                    } else {
                      onFilterChange({ ...filters, ageGroups: current.filter(a => a !== age.id) });
                    }
                  }}
                />
                <div>
                  <span className="text-[#2C302E]">{age.label}</span>
                  <span className="text-xs text-[#666B67] ml-2">{age.sublabel}</span>
                </div>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Price Range */}
        <FilterSection title="Price Range" icon={Tag} id="price">
          <div className="flex flex-wrap gap-2">
            {priceOptions.map((price) => (
              <button
                key={price.id}
                onClick={() => {
                  if (filters.priceRange === price.id) {
                    onFilterChange({ ...filters, priceRange: null });
                  } else {
                    onFilterChange({ ...filters, priceRange: price.id });
                  }
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filters.priceRange === price.id
                    ? 'bg-[#2C302E] text-white'
                    : 'bg-[#F9F8F6] text-[#666B67] hover:bg-[#E07A5F]/10'
                }`}
                data-testid={`filter-price-${price.id}`}
              >
                {price.label}
              </button>
            ))}
          </div>
        </FilterSection>

        {/* Categories */}
        <FilterSection title="Category" icon={Tag} id="category">
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  if (filters.category === cat.id) {
                    onFilterChange({ ...filters, category: null });
                  } else {
                    onFilterChange({ ...filters, category: cat.id });
                  }
                }}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  filters.category === cat.id
                    ? 'bg-[#81B29A] text-white'
                    : 'bg-[#F9F8F6] text-[#666B67] hover:bg-[#81B29A]/10'
                }`}
                data-testid={`filter-category-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </FilterSection>

        {/* Features */}
        <FilterSection title="Features" icon={Heart} id="features">
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="filter-indoor-outdoor">
              <span className="text-[#666B67]">Type:</span>
              <div className="flex gap-2">
                {['indoor', 'outdoor', 'both'].map((type) => (
                  <button
                    key={type}
                    onClick={() => onFilterChange({ ...filters, indoorOutdoor: type })}
                    className={`px-3 py-1 rounded-full text-sm capitalize ${
                      filters.indoorOutdoor === type
                        ? 'bg-[#2C302E] text-white'
                        : 'bg-[#F9F8F6] text-[#666B67]'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer" data-testid="filter-grandparent">
              <Checkbox
                checked={filters.grandparentFriendly}
                onCheckedChange={(checked) => onFilterChange({ ...filters, grandparentFriendly: checked })}
              />
              <span className="text-[#2C302E]">Grandparent Friendly</span>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer" data-testid="filter-buggy">
              <Checkbox
                checked={filters.buggyFriendly}
                onCheckedChange={(checked) => onFilterChange({ ...filters, buggyFriendly: checked })}
              />
              <span className="text-[#2C302E]">Buggy Friendly</span>
            </label>
          </div>
        </FilterSection>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 sticky bottom-0 bg-white pt-4 pb-6">
          <Button 
            variant="outline" 
            onClick={onReset}
            className="flex-1 rounded-full"
            data-testid="filter-reset-btn"
          >
            Reset
          </Button>
          <Button 
            onClick={onApply}
            className="flex-1 btn-primary"
            data-testid="filter-apply-btn"
          >
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FilterPanel;
